import retry from 'p-retry';

import { UserService } from '@/api/user/userService';
import { useUserTokensStore } from '@/stores/userTokensStore/userTokensStore';

interface GetCalculatorPayload {
  name: string;
}

export class CalculatorService {
  private static apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'api';

  public static async getUniversalLink(payload: GetCalculatorPayload): Promise<string> {
    const { name } = payload;

    const abortController = new AbortController();

    const result = await retry(
      async () => {
        const userStore = useUserTokensStore();

        const urlSearchParams = new URLSearchParams({
          name: name.toString(),
        });

        const response = await fetch(`${this.apiBaseUrl}/calculator?` + urlSearchParams, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userStore.getLoggedInUserAccessToken}`,
          },
        });

        const success = response.status >= 200 && response.status <= 299;

        if (!success) {
          const responseBody = await response.json();
          const message = responseBody.error?.message ?? responseBody.message;

          await this.handleError(response.status, message, abortController);
        }

        return response.json();
      },
      {
        retries: 2,
        signal: abortController.signal,
      },
    );

    return result.url;
  }

  private static async handleError(status: number, message: string, abortController?: AbortController): Promise<void> {
    if (status === 401) {
      const userStore = useUserTokensStore();

      if (userStore.getLoggedInUserRefreshToken) {
        const response = await UserService.refreshToken({ refreshToken: userStore.getLoggedInUserRefreshToken });

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response;

        userStore.setUserTokens({
          accessToken,
          refreshToken: newRefreshToken,
          expiresIn,
        });

        throw new Error('Token expired. Retrying...');
      }
    } else if (status === 404 && abortController) {
      abortController.abort('Resource not found.');
    } else {
      throw new Error(message);
    }
  }
}
